import React, { memo, useState } from 'react';
import { createPortal } from 'react-dom';

import DynamicIconWrapper from '@components/common/DynamicIconWrapper';
import SliderCheckbox from '@components/common/SliderCheckbox';
import Tooltip from '@components/common/Tooltip';
import SitesCellsDataIncluded from '@components/ModalComponents/SitesCellsDataIncluded';

import { Icons } from '@icons/icons';

import { IChartControlPanelTypes } from './ChartControlPanel.types';

import { EVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';

import Modal from '../Modal';

import './ChartControlPanel.scss';

const ChartControlPanel = ({
	isDisabledThreshold,
	isCheckedThreshold = false,
	chart,
	openFullCard = () => null,
	toggleThreshold,
	isExpandable,
	isDisabledExpand,
	isBackIcon,
	isDisabledExport,
	drillUp = () => null,
	prevTimeAggregationType,
	downloadChart,
	isCheckedLogarithmic = false,
	toggleLogarithmic,
	isFullCard = false,
	charts,
	isCompare = false,
	isAxisVisible = false,
	toggleAxisVisibility,
}: IChartControlPanelTypes) => {
	const [isSitesCellsDataIncludedModalOpen, setIsSitesCellsDataIncludedModalOpen] = useState(false);

	const isShowListOfSites = chart?.aggregationType?.name !== EAggregationTypes.siteLevelAggregatedCells;
	const isOranVendor = charts?.some(chart => chart.vendor.name === EVendorsValues.oran);
	const sitesCellsDataIncludedTitle = `${isOranVendor ? 'Nodes' : 'Sites'}/cells data included`;

	const openFullCardHandler = () => !isDisabledExpand && openFullCard();
	const toggleThresholdHandler = () => !isDisabledThreshold && toggleThreshold && toggleThreshold();
	const toggleLogarithmicHandler = () => toggleLogarithmic && toggleLogarithmic();

	const handleDownloadChart = () => {
		if (isDisabledExport) return;

		downloadChart();
	};

	const toggleSitesCellsDataIncludedModalOpen = () => setIsSitesCellsDataIncludedModalOpen(prevState => !prevState);

	return (
		<div className='f5gControlPanelContainer'>
			{createPortal(
				<Modal
					isOpen={isSitesCellsDataIncludedModalOpen && !!charts?.length}
					isScrollableContent
					maxHeight='80%'
					maxWidth='80%'
					minWidth='50%'
					onClose={toggleSitesCellsDataIncludedModalOpen}
					title={sitesCellsDataIncludedTitle}
					width='auto'
				>
					{charts?.length && <SitesCellsDataIncluded charts={charts} />}
				</Modal>,
				document.body,
			)}
			{isCompare
				&& <div className='f5gControlPanelSliders'>
					{!!toggleAxisVisibility
						&& <SliderCheckbox
							checked={isAxisVisible}
							id='axis-slider-checkbox-compare'
							label='Toggle axis'
							onChange={toggleAxisVisibility}
						/>
					}
					{!!toggleLogarithmic
						&& <SliderCheckbox
							checked={isCheckedLogarithmic}
							id='logarithmic-slider-checkbox-compare'
							label='Logarithmic view'
							onChange={toggleLogarithmicHandler}
						/>
					}
				</div>
			}
			{chart &&
				<div className='f5gControlPanelSliders'>
					<SliderCheckbox
						checked={isCheckedThreshold}
						disabled={isDisabledThreshold}
						id={`threshold-slider-checkbox-${chart.chartId}`}
						label='Threshold'
						onChange={toggleThresholdHandler}
					/>
					{!!toggleLogarithmic
						&& <SliderCheckbox
							checked={isCheckedLogarithmic}
							id={`logarithmic-slider-checkbox-${chart.chartId}`}
							label='Logarithmic view'
							onChange={toggleLogarithmicHandler}
						/>
					}
				</div>
			}
			<div className='f5gControlPanelIcons'>
				{isBackIcon
					&& <Tooltip
						content={`Drill up to ${prevTimeAggregationType}`}
						isDisabled={!prevTimeAggregationType}
					>
						<div onClick={drillUp}>
							<DynamicIconWrapper icon={Icons.back} />
						</div>
					</Tooltip>
				}
				{isShowListOfSites && isFullCard
					&& <div onClick={toggleSitesCellsDataIncludedModalOpen}>
						<DynamicIconWrapper icon={Icons.manageSiteDB} />
					</div>
				}
				<div onClick={handleDownloadChart}>
					<DynamicIconWrapper
						icon={Icons.export}
						isDisabled={isDisabledExport}
					/>
				</div>
				{isExpandable
					&& <Tooltip content='Maximize chart'>
						<div onClick={openFullCardHandler}>
							<DynamicIconWrapper
								icon={Icons.expandIcon}
								isDisabled={isDisabledExpand}
							/>
						</div>
					</Tooltip>
				}
			</div>
		</div>
	);
};

export default memo(ChartControlPanel);
